import { Button, Input, Typography } from 'antd';
import { UpdateIcon, TrashIcon } from 'assets';
import { Avatar, Modal } from 'components/common';
import dayjs from 'dayjs';
import { useUpdateContactNote } from 'hooks/contacts';
import { useDeleteContactNote } from 'hooks/contacts/useDeleteContactNote';
import { messages } from 'messages';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import React, { useEffect } from 'react';
import { ContactDto } from 'types';

export const ContactNote = ({
  note,
  contactId,
  customerName
}: {
  note: ContactDto['note'];
  user: ContactDto['updatedBy'];
  contactId: number;
  customerName: string;
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const [open, setOpen] = React.useState(false);

  const { onUpdateContactHandle, isLoading: isUpdatingNote } = useUpdateContactNote(contactId);
  const { onDeleteContactHandle, isLoading: isDeletingNote } = useDeleteContactNote(contactId);
  const handelDeleteNote = () => {
    modal.confirm({
      title: (
        <Typography.Text className='text-lg font-semibold'>{customerLookupMessages.deleteNoteConfirm}</Typography.Text>
      ),
      content: (
        <>
          <Typography.Paragraph className='mt-4'>{customerLookupMessages.deleteNoteWarning}</Typography.Paragraph>
        </>
      ),
      okText: messages.delete,
      okType: 'danger',
      cancelText: messages.cancel,
      onOk: () => {
        if (note?.contactNoteId) onDeleteContactHandle({ contactNoteId: note?.contactNoteId }, () => {});
      }
    });
  };

  const handleUpdateNote = (newNote: string) => {
    onUpdateContactHandle({ note: newNote }, () => {
      setOpen(false);
    });
  };

  const userEditNote = note?.updatedByUser || note?.createdByUser;
  const updatedAt = note?.updatedAt || note?.createdAt;

  return (
    <>
      {note?.note ? (
        <div className='rounded-md border border-error bg-white p-5'>
          <div className='mb-4 flex items-center gap-4'>
            <Avatar size={44} name={userEditNote?.fullName} fileId={userEditNote?.avatar} />
            <div className='flex flex-1 flex-col gap-1'>
              <Typography.Text className='font-semibold'>{userEditNote?.fullName}</Typography.Text>
              <Typography.Text className='text-xs'>
                {updatedAt ? dayjs(updatedAt).format('HH:mm, DD/MM/YYYY') : ''}
              </Typography.Text>
            </div>
            <div className='ml-auto mt-0.5 flex items-center gap-2'>
              <div
                onClick={handelDeleteNote}
                className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border hover:bg-colorBgItemHover'
              >
                <TrashIcon className='h-4 w-4' />
              </div>

              <div
                onClick={() => {
                  setOpen(true);
                }}
                className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border hover:bg-colorBgItemHover'
              >
                <UpdateIcon className='h-4 w-4' />
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-2'>
            <Typography.Text className='text-xs font-semibold'>{messages.warning}</Typography.Text>
            <Typography.Paragraph className='mb-0 whitespace-pre-wrap'>{note?.note ?? ''}</Typography.Paragraph>
          </div>
          {contextHolder}
        </div>
      ) : (
        <div onClick={() => setOpen(true)} className='mt-2 flex items-center gap-2'>
          <UpdateIcon className='h-4 w-4' /> <Typography.Link className='text-sm'>{messages.addNote}</Typography.Link>
        </div>
      )}
      <UpdateContactNoteModal
        isLoading={isUpdatingNote}
        open={open}
        onCancel={() => setOpen(false)}
        note={note}
        onSave={handleUpdateNote}
        customerName={customerName}
      />
    </>
  );
};

export const UpdateContactNoteModal: React.FC<{
  open: boolean;
  onCancel: () => void;
  note?: ContactDto['note'];
  onSave?: (note: string) => void;
  isLoading?: boolean;
  customerName?: string;
}> = ({ open, onCancel, note, onSave, isLoading, customerName }) => {
  const [noteState, setNote] = React.useState(note?.note || '');

  const handleSave = () => {
    onSave?.(noteState);
  };

  useEffect(() => {
    setNote(note?.note || '');
  }, [note]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <div className='flex items-center justify-start'>
          <Typography.Text className='font-semibold'>{customerLookupMessages.note}</Typography.Text>
        </div>
      }
      styles={{
        body: {
          padding: '32px 24px 64px 24px'
        }
      }}
      width={568}
      footer={
        <div className='flex justify-end gap-3'>
          <Button onClick={handleSave} loading={isLoading} type='primary'>
            {messages.save}
          </Button>
        </div>
      }
    >
      <div>
        <Typography.Text className='text-sm text-colorTextPlaceholder'>
          {customerLookupMessages.editNote(customerName || '')}
        </Typography.Text>
        <Input.TextArea className='mt-6' value={noteState} onChange={(e) => setNote(e.target.value)} rows={4} />
      </div>
    </Modal>
  );
};
